import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { extractStoragePath } from '@/utils/imageResize';
import { computeListingCompletion } from '@/lib/listingCompletion';
import { parseIdentityStatus } from '@/types/identity';
import { deriveOwnershipBadge } from '@/lib/ownership';
import type { Listing } from '@/types/listing';

export interface HeaderProfile {
  role: string | null;
  displayName: string | null;
  /** Account first + last name (from `users`), for the header hover callout. */
  fullName: string | null;
  /** Signed, ready-to-render avatar URL (or null when no photo). */
  avatarUrl: string | null;
  /** Owner only: the flat's address line, shown under "Profil Mieszkania". */
  ownerAddress: string | null;
  /** Owner only: listing completeness % — the header nudge (Piotr 2026-07-15);
   * null without a listing. */
  ownerCompletionPct: number | null;
  /** Owner only: both identity + ownership verified — surfaces a trust badge in
   * the nav (Piotr 2026-07-22). */
  ownerVerified: boolean;
  loading: boolean;
}

const EMPTY: HeaderProfile = {
  role: null,
  displayName: null,
  fullName: null,
  avatarUrl: null,
  ownerAddress: null,
  ownerCompletionPct: null,
  ownerVerified: false,
  loading: false,
};

const LOADING: HeaderProfile = { ...EMPTY, loading: true };

/**
 * Minimal profile read for the top navigation: role (which nav variant to
 * show), display name + avatar (the "Mój Profil" entry), and — for owners — the
 * flat address shown under "Profil Mieszkania". Deliberately lighter than
 * `useProfileData`: it runs for any logged-in user, so it only touches `users`,
 * `tenant_profiles`, and (owners only) the latest listing.
 */
export function useHeaderProfile(session: Session | null): HeaderProfile {
  // Keyed on the auth user id, never on the session object: auth-js hands out a
  // new session object on every TOKEN_REFRESHED (hourly, and at once when a
  // backgrounded tab comes back), and re-fetching with `loading` on each one
  // blanked the nav and avatar for a beat (Maciek 2026-10-26). The loaded
  // profile remembers whose it is (`forUser`), so a different account or a
  // sign-out never shows the previous user's name, not even for one render.
  const userId = session?.user?.id ?? null;
  const [state, setState] = useState<{ forUser: string | null; profile: HeaderProfile }>({
    forUser: null,
    profile: EMPTY,
  });
  const queryClient = useQueryClient();
  // The header badge lives in the persistent layout, so its fetch keys on the
  // user only. Bump this when a listing/verification save invalidates the
  // shared caches so the completeness % refreshes live, without a page reload
  // (Piotr 2026-07-22). Content edits invalidate ['flat.my_listing'].
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    const unsub = queryClient.getQueryCache().subscribe((event) => {
      const key = event?.query?.queryKey?.[0];
      if (key === 'flat.my_listing' || key === 'flat.listings') {
        setRefreshTick((t) => t + 1);
      }
    });
    return unsub;
  }, [queryClient]);

  useEffect(() => {
    if (!userId) {
      setState({ forUser: null, profile: EMPTY });
      return;
    }
    let cancelled = false;

    (async () => {
      const { data: userRow, error } = await supabase
        .from('users')
        .select('id, role, first_name, last_name')
        .eq('auth_id', userId)
        .maybeSingle();

      if (cancelled) return;
      if (!userRow) {
        // A failed background refresh (listing save) keeps what is on screen;
        // a failed first read settles empty rather than spinning forever.
        setState((prev) => (error && prev.forUser === userId ? prev : { forUser: userId, profile: EMPTY }));
        return;
      }

      const role = userRow.role ?? null;
      // Agents are NOT owners: they have no listing and no landlord console, so
      // don't query flat.listings for them (Maciek 2026-07-29).
      const isOwner = role === 'owner';
      const fullName =
        `${userRow.first_name ?? ''} ${userRow.last_name ?? ''}`.trim() || null;

      const { data: profile } = await supabase
        .from('tenant_profiles')
        .select('display_name, photo_url')
        .eq('user_id', userRow.id)
        .maybeSingle();

      if (cancelled) return;

      let avatarUrl: string | null = null;
      if (profile?.photo_url) {
        avatarUrl = profile.photo_url;
        try {
          const storagePath = extractStoragePath(profile.photo_url);
          if (storagePath) {
            const { data: signed } = await supabase.storage
              .from('profile-photos')
              .createSignedUrl(storagePath, 3600);
            if (signed?.signedUrl) avatarUrl = signed.signedUrl;
          }
        } catch {
          /* keep the raw url as a best-effort fallback */
        }
      }

      let ownerAddress: string | null = null;
      let ownerCompletionPct: number | null = null;
      let ownerVerified = false;
      if (isOwner) {
        try {
          const { data: listings } = await supabase
            .schema('flat' as never)
            .from('listings' as never)
            .select('*')
            .eq('landlord_id', userId)
            .order('updated_at', { ascending: false })
            .limit(1);
          const row = (listings as unknown as Listing[] | null)?.[0];
          ownerAddress = row?.address_line ?? row?.title ?? null;
          // Header completeness nudge — the % next to "Profil Mieszkania". Must
          // fold in identity + ownership verification (15% each), SAME as the
          // dashboard/hub, or the header reads 15% low (Piotr 2026-07-22). This
          // hook computes in an async effect (no hook context), so it fetches
          // the verification sessions and reuses the same pure completion module.
          if (row) {
            const [idSess, ownSess] = await Promise.all([
              supabase.rpc('get_my_identity_status' as never),
              supabase
                .schema('flat' as never)
                .from('ownership_check_sessions' as never)
                .select('status,external_id')
                .eq('listing_id', row.id)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle(),
            ]);
            // The ID check's state only from get_my_identity_status() (docs/TenantIdVerification.md §12).
            const idRaw = idSess.data as unknown;
            const identityVerified =
              parseIdentityStatus(Array.isArray(idRaw) ? idRaw[0] : idRaw)?.state === 'verified';
            const ownershipVerified = deriveOwnershipBadge(ownSess.data as never) === 'verified';
            ownerVerified = identityVerified && ownershipVerified;
            ownerCompletionPct = computeListingCompletion(row, {
              identityVerified,
              ownershipVerified,
            }).overallPct;
          }
        } catch {
          /* address subtitle is best-effort */
        }
      }

      if (cancelled) return;
      setState({
        forUser: userId,
        profile: {
          role,
          displayName: profile?.display_name ?? null,
          fullName,
          avatarUrl,
          ownerAddress,
          ownerCompletionPct,
          ownerVerified,
          loading: false,
        },
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, refreshTick]);

  if (!userId) return EMPTY;
  // First load for this user, or a different account than the one loaded: the
  // only time the header holds back its role-dependent entries.
  if (state.forUser !== userId) return LOADING;
  return state.profile;
}
